import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import {
  useAdminCertificatesQuery,
  useCertificateStatsQuery,
  useCertificateBatchesQuery,
  useCertificateEventsQuery,
} from "@/components/admin/certificates/useCertificatesQueries";
import {
  useIssueCertificateMutation,
  useRevokeCertificateMutation,
  useIssueSingleCertificateMutation,
  useBulkIssueCertificatesMutation,
  useRegenerateCertificateMutation,
} from "@/components/admin/certificates/useCertificatesMutations";
import {
  searchUsers,
  fetchLearningTracks,
  fetchAllCourses,
  fetchTeams,
  downloadCertificateBlob,
} from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { PaginationControls } from "@/components/PaginationControls";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { TableSkeleton, SectionLoader } from "@/components/loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Award,
  Search,
  Plus,
  ShieldOff,
  Users,
  User,
  GraduationCap,
  BookOpen,
  FileText,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  Eye,
  Copy,
  Ban,
  Package,
  Filter,
  FilterX,
  RefreshCw,
  Download,
  AlertTriangle,
  History,
  Info,
  Hash,
  Clock,
} from "lucide-react";
import { Certificate, CertificateBatch, BulkIssueRequest } from "@/types/certificate";

export const Route = createFileRoute("/_authenticated/admin/certificates")({
  component: AdminCertificatesPage,
});

function AdminCertificatesPage() {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  if (!hasPermission("view:certificates")) {
    return <AccessDenied message="You don't have permission to view certificates." />;
  }

  return (
    <div className="space-y-6 font-sans">
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-primary/10 text-primary shadow-inner">
              <Award className="h-7 w-7" />
            </div>
            Certificate Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            Issue new credentials, track generation progress, and review the audit history.
          </p>
        </div>
      </div>

      <StatsWidget statusFilter={statusFilter} setStatusFilter={setStatusFilter} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-muted/40 p-1 rounded-xl mb-4 border max-w-fit">
          <TabsTrigger value="all" className="gap-1.5 rounded-lg px-4 py-2 font-semibold">
            <FileText className="h-4 w-4" /> All Certificates
          </TabsTrigger>
          {hasPermission("issue:certificates") && (
            <TabsTrigger value="issue" className="gap-1.5 rounded-lg px-4 py-2 font-semibold">
              <Plus className="h-4 w-4" /> Issue New
            </TabsTrigger>
          )}
          {/* Batch history is bulk-issuance data that only spans the whole org and is
              SystemAdmin-only on the API (GET /admin/certificates/batches). Hide the tab from
              TeamLeaders (who reach this page via view:certificates) so they don't land on a
              403-turned-empty panel; issue:certificates is granted to SystemAdmin only. */}
          {hasPermission("issue:certificates") && (
            <TabsTrigger value="batches" className="gap-1.5 rounded-lg px-4 py-2 font-semibold">
              <Package className="h-4 w-4" /> Batch History
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all" className="mt-0">
          <CertificatesTable statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
        </TabsContent>
        <TabsContent value="issue" className="mt-0">
          <IssueWizard onComplete={() => setTab("all")} />
        </TabsContent>
        {hasPermission("issue:certificates") && (
          <TabsContent value="batches" className="mt-0">
            <BatchHistory />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ──────────────────────── Stats Widget ──────────────────────── */

interface StatsWidgetProps {
  statusFilter: string;
  setStatusFilter: (status: string) => void;
}

function StatsWidget({ statusFilter, setStatusFilter }: StatsWidgetProps) {
  const { data: stats, isLoading } = useCertificateStatsQuery();

  if (isLoading || !stats) {
    return (
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-7">
        {Array.from({ length: 7 }).map((_, i) => (
          <Card key={i} className="animate-pulse border rounded-2xl">
            <CardContent className="p-4">
              <div className="h-3 bg-muted rounded w-16 mb-2" />
              <div className="h-7 bg-muted rounded w-10" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statItems = [
    { label: "Total", value: stats.total, status: "all" },
    { label: "Issued", value: stats.issued, status: "issued" },
    { label: "Pending", value: stats.pending, status: "pending" },
    { label: "Generating", value: stats.generating, status: "generating" },
    { label: "Failed", value: stats.failed, status: "failed" },
    { label: "Revoked", value: stats.revoked, status: "revoked" },
    { label: "Replaced", value: stats.replaced, status: "replaced" },
  ];

  return (
    <div className="grid gap-4 grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 animate-in fade-in slide-in-from-top-4 duration-500">
      {statItems.map((item, index) => {
        const isSelected = statusFilter === item.status;
        return (
          <button
            key={item.label}
            onClick={() => setStatusFilter(item.status)}
            className={cn(
              "text-left rounded-2xl border transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-95 cursor-pointer flex flex-col justify-between overflow-hidden relative group/card",
              isSelected
                ? "border-primary bg-primary/5 shadow-md shadow-primary/10 scale-102"
                : "border-muted/70 hover:border-primary/30 bg-card",
            )}
            style={{
              animationDelay: `${index * 40}ms`,
              animationFillMode: "both",
            }}
          >
            {/* Top accent line if selected */}
            {isSelected && (
              <div className="absolute top-0 left-0 w-full h-1 bg-primary animate-in fade-in duration-300" />
            )}

            <div className="p-4 w-full">
              <p
                className={cn(
                  "text-xs font-semibold uppercase tracking-wider transition-colors duration-200",
                  isSelected ? "text-primary" : "text-muted-foreground",
                )}
              >
                {item.label}
              </p>
              <p className="text-2xl font-black mt-1.5 tracking-tight text-primary transition-transform duration-300 group-hover/card:scale-105">
                {item.value}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────── Tab 1: Certificates Table ──────────────────────── */

interface CertificatesTableProps {
  statusFilter: string;
  setStatusFilter: (status: string) => void;
}

function CertificatesTable({ statusFilter, setStatusFilter }: CertificatesTableProps) {
  const { hasPermission } = useAuth();
  const pagination = usePaginationState({ initialPageSize: 20 });
  const { pageNumber, pageSize, search, setSearch, setPageNumber, setPageSize } = pagination;
  const [typeFilter, setTypeFilterState] = useState<string>("all");
  const [dateFromFilter, setDateFromFilter] = useState<string>("");
  const [dateToFilter, setDateToFilter] = useState<string>("");

  // status/type changes must also reset back to page 1, same as search does.
  const setTypeFilter = (value: string) => {
    setTypeFilterState(value);
    setPageNumber(1);
  };
  // statusFilter is owned by the parent (both the Select below and StatsWidget's tiles can
  // change it), so reset pagination back to page 1 whenever it changes, regardless of which
  // UI triggered it.
  useEffect(() => {
    setPageNumber(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Dialog States
  const [revokeTarget, setRevokeTarget] = useState<Certificate | null>(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [regenTarget, setRegenTarget] = useState<Certificate | null>(null);
  const [eventsTarget, setEventsTarget] = useState<Certificate | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<Certificate | null>(null);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Queries & Mutations — status/type/pageNumber/pageSize are filtered server-side; search
  // text + date ranges are applied client-side over the current page below (the API has
  // no matching parameters for those yet).
  const { data, isLoading, isFetching } = useAdminCertificatesQuery(
    statusFilter,
    typeFilter,
    pageNumber,
    pageSize,
  );
  const certs = data?.items ?? [];
  const issueMut = useIssueCertificateMutation();
  const revokeMut = useRevokeCertificateMutation();
  const regenMut = useRegenerateCertificateMutation();

  const filtered = useMemo(() => {
    return certs.filter((c) => {
      // Search Text
      if (search.trim()) {
        const query = search.toLowerCase();
        const name = (c.user?.full_name || "").toLowerCase();
        const email = (c.user?.email || "").toLowerCase();
        const code = (c.cert_code || "").toLowerCase();
        if (!name.includes(query) && !email.includes(query) && !code.includes(query)) return false;
      }

      // Date From Filter
      if (dateFromFilter) {
        const certDate = new Date(c.issued_at || c.created_at).setHours(0, 0, 0, 0);
        const fromDate = new Date(dateFromFilter).setHours(0, 0, 0, 0);
        if (certDate < fromDate) return false;
      }

      // Date To Filter
      if (dateToFilter) {
        const certDate = new Date(c.issued_at || c.created_at).setHours(0, 0, 0, 0);
        const toDate = new Date(dateToFilter).setHours(0, 0, 0, 0);
        if (certDate > toDate) return false;
      }

      return true;
    });
  }, [certs, search, dateFromFilter, dateToFilter]);

  const getTitle = (c: Certificate) => {
    if (c.cert_type === "track") return c.track?.name || c.custom_title || "Track Certificate";
    if (c.cert_type === "course") return c.course?.title || c.custom_title || "Course Certificate";
    return c.custom_title || "Custom Certificate";
  };

  const statusBadge = (s: string) => {
    switch (s) {
      case "pending":
        return (
          <Badge variant="outline" className="gap-1 border-warning/20 text-warning bg-warning/10">
            <Loader2 className="h-3 w-3" />
            Pending
          </Badge>
        );
      case "generating":
        return (
          <Badge
            variant="outline"
            className="gap-1 border-primary/20 text-primary bg-primary/10 animate-pulse"
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            Generating
          </Badge>
        );
      case "issued":
        return (
          <Badge className="bg-success-muted text-success border border-success/20 gap-1 hover:bg-success-muted shadow-none">
            <CheckCircle2 className="h-3 w-3 text-success" />
            Issued
          </Badge>
        );
      case "revoked":
        return (
          <Badge
            variant="outline"
            className="gap-1 border-destructive/20 text-destructive bg-destructive/10"
          >
            <XCircle className="h-3 w-3" />
            Revoked
          </Badge>
        );
      case "replaced":
        return (
          <Badge
            variant="secondary"
            className="gap-1 bg-muted text-muted-foreground border border-muted-foreground/10"
          >
            <Copy className="h-3 w-3" />
            Replaced
          </Badge>
        );
      case "failed":
      default:
        return (
          <Badge
            variant="outline"
            className="gap-1 bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/15"
          >
            <AlertTriangle className="h-3 w-3" />
            Failed
          </Badge>
        );
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success("Copied to clipboard");
  };

  const handleDownloadPdf = async (id: string) => {
    setDownloadingId(id);
    try {
      await downloadCertificateBlob(id);
      toast.success("Certificate downloaded.");
    } catch (e: any) {
      toast.error(e.message || "Failed to download PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card className="rounded-2xl border border-muted/70 shadow-sm overflow-hidden">
      <CardHeader className="pb-4">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <CardTitle className="text-lg font-bold text-foreground">
              All Certificates ({data?.totalCount ?? filtered.length})
            </CardTitle>
            {(search ||
              statusFilter !== "all" ||
              typeFilter !== "all" ||
              dateFromFilter ||
              dateToFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                  setTypeFilter("all");
                  setDateFromFilter("");
                  setDateToFilter("");
                }}
                className="text-xs text-muted-foreground hover:text-destructive h-8 px-2.5 rounded-lg"
              >
                <FilterX className="h-3.5 w-3.5 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
              <Input
                placeholder="Search recipient or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-xs"
              />
            </div>

            {/* Status */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 rounded-xl bg-muted/20 border-muted/70 focus:ring-primary text-xs text-foreground">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="generating">Generating</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
                <SelectItem value="replaced">Replaced</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            {/* Certificate Type */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-10 rounded-xl bg-muted/20 border-muted/70 focus:ring-primary text-xs text-foreground">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="track">Learning Track</SelectItem>
                <SelectItem value="course">Single Course</SelectItem>
                <SelectItem value="custom">Custom Title</SelectItem>
              </SelectContent>
            </Select>

            {/* Date From */}
            <div className="relative">
              <Input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                title="Issued On or After"
              />
            </div>

            {/* Date To */}
            <div className="relative">
              <Input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark]"
                title="Issued On or Before"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={7} columns={7} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground border-t bg-muted/5">
            <Award className="h-10 w-10 mx-auto mb-3 opacity-30 text-primary" />
            <p className="text-sm font-medium">No certificates match the filter options.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border-t">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Certificate</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y">
                {filtered.map((c, index) => (
                  <TableRow
                    key={c.id}
                    className="hover:bg-muted/10 transition-colors animate-in fade-in duration-300"
                    style={{ animationDelay: `${index * 30}ms`, animationFillMode: "both" }}
                  >
                    <TableCell>
                      <div>
                        <p className="font-semibold text-sm text-foreground">
                          {c.user?.full_name || "Unknown"}
                        </p>
                        <p className="text-xs text-muted-foreground">{c.user?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell
                      className="font-semibold text-sm max-w-48 truncate"
                      title={getTitle(c)}
                    >
                      {getTitle(c)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className="capitalize text-[10px] font-bold border"
                      >
                        {c.cert_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {c.cert_code ? (
                        <button
                          onClick={() => copyCode(c.cert_code!)}
                          className="font-mono text-xs text-primary hover:underline flex items-center gap-1.5 font-bold"
                        >
                          {c.cert_code} <Copy className="h-3 w-3 opacity-70" />
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>{statusBadge(c.status)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-medium">
                      {new Date(c.issued_at || c.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.status === "pending" && (
                          <Button
                            size="sm"
                            variant="default"
                            className="h-8 text-xs gap-1 bg-primary hover:bg-primary/95 text-primary-foreground font-semibold rounded-lg cursor-pointer"
                            onClick={() => {
                              issueMut.mutate(c.id, {
                                onSuccess: (data) =>
                                  toast.success(data.message || "Issuance queued successfully."),
                              });
                            }}
                            disabled={issueMut.isPending}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Issue
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 rounded-lg hover:bg-muted/70"
                          onClick={() => setDetailsTarget(c)}
                          title="View Details"
                        >
                          <Info className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 rounded-lg hover:bg-muted/70"
                          onClick={() => setEventsTarget(c)}
                          title="View Events History"
                        >
                          <History className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                        {c.status === "issued" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs gap-1 rounded-lg border-primary/20 hover:bg-primary/5 hover:text-primary transition-colors text-primary"
                              onClick={() => handleDownloadPdf(c.id)}
                              disabled={downloadingId === c.id}
                            >
                              {downloadingId === c.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Download className="h-3.5 w-3.5" />
                              )}
                              PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs gap-1 rounded-lg hover:bg-muted"
                              onClick={() => window.open(`/verify/${c.cert_code}`, "_blank")}
                            >
                              <Eye className="h-3.5 w-3.5 text-muted-foreground" /> Verify
                            </Button>
                            {hasPermission("issue:certificates") && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs gap-1 rounded-lg hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200"
                                  onClick={() => setRegenTarget(c)}
                                >
                                  <RefreshCw className="h-3.5 w-3.5" /> Re-gen
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="h-8 text-xs gap-1 rounded-lg cursor-pointer"
                                  onClick={() => setRevokeTarget(c)}
                                >
                                  <Ban className="h-3.5 w-3.5" /> Revoke
                                </Button>
                              </>
                            )}
                          </>
                        )}
                        {c.status === "failed" && hasPermission("issue:certificates") && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs gap-1 rounded-lg hover:bg-amber-50 hover:text-amber-700"
                            onClick={() => setRegenTarget(c)}
                          >
                            <RefreshCw className="h-3.5 w-3.5" /> Retry
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && (
          <PaginationControls
            pageNumber={data.pageNumber}
            pageSize={data.pageSize}
            totalPages={data.totalPages}
            totalCount={data.totalCount}
            hasNextPage={data.hasNextPage}
            hasPreviousPage={data.hasPreviousPage}
            onPageChange={setPageNumber}
            onPageSizeChange={setPageSize}
            isLoading={isFetching}
          />
        )}
      </CardContent>

      {/* Revoke Dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <DialogContent className="rounded-2xl border bg-card shadow-lg sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive text-xl font-bold">
              <ShieldOff className="h-5 w-5 animate-pulse" /> Revoke Certificate
            </DialogTitle>
            <DialogDescription className="text-sm">
              This action is irreversible. The certificate for{" "}
              <strong>{revokeTarget?.user?.full_name}</strong> will be permanently marked as
              revoked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs font-semibold text-muted-foreground">
              Reason for revocation *
            </Label>
            <Textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              placeholder="Explain why this certificate is being revoked..."
              rows={3}
              className="rounded-xl focus-visible:ring-primary text-foreground"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setRevokeTarget(null)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!revokeReason.trim() || revokeMut.isPending}
              onClick={() => {
                if (revokeTarget) {
                  revokeMut.mutate(
                    { id: revokeTarget.id, reason: revokeReason },
                    {
                      onSuccess: () => {
                        toast.success("Certificate revoked successfully.");
                        setRevokeTarget(null);
                        setRevokeReason("");
                      },
                    },
                  );
                }
              }}
              className="rounded-xl cursor-pointer"
            >
              {revokeMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1 inline" />
              ) : null}
              Confirm Revocation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Dialog */}
      <Dialog open={!!regenTarget} onOpenChange={() => setRegenTarget(null)}>
        <DialogContent className="rounded-2xl border bg-card shadow-lg sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground text-xl font-bold">
              <RefreshCw
                className="h-5 w-5 text-primary animate-spin"
                style={{ animationDuration: "3s" }}
              />{" "}
              Regenerate Certificate PDF
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              This will re-run the QuestPDF generation engine on the server for{" "}
              <strong>{regenTarget?.user?.full_name}</strong>'s certificate. The live certificate
              code will remain valid while regeneration runs.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button variant="outline" onClick={() => setRegenTarget(null)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={regenMut.isPending}
              onClick={() => {
                if (regenTarget) {
                  regenMut.mutate(regenTarget.id, {
                    onSuccess: () => {
                      toast.success("Regeneration started in background.");
                      setRegenTarget(null);
                    },
                    onError: (err: any) => {
                      toast.error(err.message || "Failed to start regeneration.");
                    },
                  });
                }
              }}
              className="rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold cursor-pointer"
            >
              {regenMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1 inline" /> : null}
              Confirm Regeneration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Events Log Dialog */}
      <EventsDialog certificate={eventsTarget} onClose={() => setEventsTarget(null)} />

      {/* Metadata Details Dialog */}
      <DetailsDialog
        certificate={detailsTarget}
        resolvedTitle={detailsTarget ? getTitle(detailsTarget) : ""}
        onClose={() => setDetailsTarget(null)}
      />
    </Card>
  );
}

/* ──────────────────────── Details Dialog ──────────────────────── */

interface DetailsDialogProps {
  certificate: Certificate | null;
  resolvedTitle: string;
  onClose: () => void;
}

function DetailsDialog({ certificate, resolvedTitle, onClose }: DetailsDialogProps) {
  const isOpen = !!certificate;
  const c = certificate;

  const Row = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="flex flex-col gap-1 py-2.5 border-b last:border-b-0">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
        {label}
      </span>
      <span className="text-sm text-foreground break-words">{children}</span>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl border bg-card shadow-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <Info className="h-5 w-5 text-primary" /> Certificate Details
          </DialogTitle>
          <DialogDescription className="text-sm">
            The metadata snapshot stored on this certificate — the exact values the PDF and public
            verification page render.
          </DialogDescription>
        </DialogHeader>
        {c && (
          <div className="py-1 max-h-[28rem] overflow-y-auto custom-scrollbar pr-2">
            <Row label="Recipient">
              <span className="font-semibold">{c.user?.full_name || "Unknown"}</span>
              {c.user?.email ? (
                <span className="text-muted-foreground"> · {c.user.email}</span>
              ) : null}
            </Row>
            <Row label="Resolved Title">
              <span className="font-medium">{resolvedTitle}</span>
              {c.custom_title && c.custom_title !== resolvedTitle ? (
                <span className="ml-2 inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                  custom override
                </span>
              ) : null}
            </Row>
            <Row label="Training Hours">
              {c.custom_hours != null ? (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {c.custom_hours} hours
                </span>
              ) : (
                <span className="text-muted-foreground">Not specified</span>
              )}
            </Row>
            <Row label="Type">
              <span className="capitalize">{c.cert_type}</span>
            </Row>
            <Row label="Credential Code">
              {c.cert_code ? (
                <span className="font-mono text-xs">{c.cert_code}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Row>
            <Row label="Status">
              <span className="capitalize">{c.status}</span>
            </Row>
            <Row label="Issuance">
              {c.batch_id ? (
                <span className="inline-flex items-center gap-1.5">
                  <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                  Bulk batch
                  <span className="font-mono text-xs text-muted-foreground">{c.batch_id}</span>
                </span>
              ) : (
                <span className="text-muted-foreground">Single issuance</span>
              )}
            </Row>
            <Row label="Created">{new Date(c.created_at).toLocaleString()}</Row>
            <Row label="Issued">
              {c.issued_at ? new Date(c.issued_at).toLocaleString() : "Not yet issued"}
            </Row>
            {c.pdf_url ? (
              <div className="pt-3">
                <a
                  href={c.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  <Download className="h-4 w-4" /> View generated PDF
                </a>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────── Events Dialog ──────────────────────── */

interface EventsDialogProps {
  certificate: Certificate | null;
  onClose: () => void;
}

function EventsDialog({ certificate, onClose }: EventsDialogProps) {
  const isOpen = !!certificate;
  const { data: events = [], isLoading } = useCertificateEventsQuery(certificate?.id || "", isOpen);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-2xl border bg-card shadow-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <History className="h-5 w-5 text-primary" /> Audit Log History
          </DialogTitle>
          <DialogDescription className="text-sm">
            Audit trail records for Certificate ID:{" "}
            <span className="font-mono text-xs bg-muted/80 text-foreground px-2 py-0.5 rounded border border-muted-foreground/10">
              {certificate?.id}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 max-h-96 overflow-y-auto custom-scrollbar pr-2">
          {isLoading ? (
            <SectionLoader label="Loading event history…" />
          ) : events.length === 0 ? (
            <p className="text-sm text-center text-muted-foreground py-8">
              No event records found for this certificate.
            </p>
          ) : (
            <div className="space-y-4">
              {events.map((e) => (
                <div key={e.id} className="border-l-2 border-primary/50 pl-3 py-1 space-y-1">
                  <div className="flex justify-between items-start gap-2">
                    <span className="font-bold text-sm text-foreground capitalize">
                      {e.event_type.replace(/_/g, " ")}
                    </span>
                    <span className="text-[10px] text-muted-foreground font-semibold shrink-0">
                      {new Date(e.created_at).toLocaleString()}
                    </span>
                  </div>
                  {e.detail && (
                    <p className="text-xs text-muted-foreground/80 leading-relaxed">{e.detail}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-xl">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────── Tab 2: Issue Wizard ──────────────────────── */

function IssueWizard({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(1);

  // Wizard state
  const [certType, setCertType] = useState<"track" | "course" | "">("");
  const [trackId, setTrackId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customHours, setCustomHours] = useState("");
  const [forceIssue, setForceIssue] = useState(false);

  // Recipient selection — exactly two supported flows: a single student, or a group.
  const [recipientMode, setRecipientMode] = useState<"single" | "team" | "">("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [userSearch, setUserSearch] = useState("");

  // Data queries
  const { data: tracks = [] } = useQuery({
    queryKey: ["all-tracks"],
    enabled: typeof window !== "undefined",
    queryFn: fetchLearningTracks,
  });

  const { data: courses = [] } = useQuery({
    queryKey: ["all-courses"],
    enabled: typeof window !== "undefined",
    queryFn: fetchAllCourses,
  });

  const { data: teams = [] } = useQuery({
    queryKey: ["all-teams"],
    enabled: typeof window !== "undefined",
    queryFn: fetchTeams,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["search-users", userSearch],
    enabled: typeof window !== "undefined" && userSearch.length >= 2,
    queryFn: () => searchUsers(userSearch),
  });

  // Mutations
  const singleIssueMut = useIssueSingleCertificateMutation();
  const bulkIssueMut = useBulkIssueCertificatesMutation();

  const canProceedStep1 = !!certType;
  const canProceedStep2 =
    (certType === "track" && !!trackId) || (certType === "course" && !!courseId);
  const canProceedStep3 =
    (recipientMode === "single" && !!selectedUserId) ||
    (recipientMode === "team" && !!selectedTeamId);

  const handleSubmit = () => {
    const sourceId = certType === "track" ? trackId : courseId;

    if (recipientMode === "single") {
      singleIssueMut.mutate(
        {
          student_id: selectedUserId,
          cert_type: certType,
          source_id: sourceId,
          custom_title: customTitle || null,
          custom_hours: customHours ? parseInt(customHours, 10) : null,
          force: forceIssue,
        },
        {
          onSuccess: () => {
            toast.success("Certificate issued successfully!");
            onComplete();
          },
          onError: (err: any) => {
            toast.error(err.message || "Failed to issue certificate.");
          },
        },
      );
    } else {
      const bulkRequest: BulkIssueRequest = {
        cert_type: certType,
        source_id: certType === "track" ? trackId : courseId,
        only_completed: !forceIssue,
        label: `Bulk: ${certType === "track" ? tracks.find((t) => t.id === trackId)?.name : courses.find((c) => c.id === courseId)?.title}`,
        group_id: selectedTeamId,
      };

      bulkIssueMut.mutate(bulkRequest, {
        onSuccess: () => {
          toast.success("Bulk issuance processing started!");
          onComplete();
        },
        onError: (err: any) => {
          toast.error(err.message || "Failed to start bulk issuance.");
        },
      });
    }
  };

  const isPending = singleIssueMut.isPending || bulkIssueMut.isPending;

  // Known defaults for the selected source, surfaced in Step 2 so the admin can see
  // what the certificate will say before deciding whether to override anything.
  const selectedCourse = certType === "course" ? courses.find((c) => c.id === courseId) : undefined;
  const selectedTrack = certType === "track" ? tracks.find((t) => t.id === trackId) : undefined;
  const defaultTitle = selectedCourse?.title ?? selectedTrack?.name ?? "";
  // Only courses carry a duration in the data model; a track has no hours field.
  const defaultHours = selectedCourse?.duration_estimate_hours ?? null;

  const getPreviewRecipient = () => {
    if (recipientMode === "single")
      return users.find((u) => u.id === selectedUserId)?.full_name || userSearch || "Student Name";
    if (recipientMode === "team")
      return `Group: ${teams.find((t) => t.id === selectedTeamId)?.name || ""}`;
    return "Student Name";
  };

  return (
    <div className="max-w-4xl mx-auto py-4">
      {/* Premium Progress Stepper */}
      <div className="flex items-center justify-between mb-10 relative">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-muted rounded-full overflow-hidden -z-10">
          <div
            className="h-full bg-primary transition-all duration-500 ease-in-out"
            style={{ width: `${((step - 1) / 3) * 100}%` }}
          />
        </div>
        {[
          { num: 1, label: "Certificate Type" },
          { num: 2, label: "Configuration" },
          { num: 3, label: "Recipients" },
          { num: 4, label: "Review & Issue" },
        ].map((s) => (
          <div key={s.num} className="flex flex-col items-center gap-2 bg-background px-2">
            <div
              className={`h-10 w-10 rounded-full flex items-center justify-center text-base font-bold transition-all duration-300 shadow-sm ${
                step >= s.num
                  ? "bg-primary text-primary-foreground scale-110 ring-4 ring-primary/20"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {step > s.num ? <CheckCircle2 className="h-5 w-5" /> : s.num}
            </div>
            <span
              className={`text-xs font-semibold ${step >= s.num ? "text-primary" : "text-muted-foreground"}`}
            >
              {s.label}
            </span>
          </div>
        ))}
      </div>

      <div className="min-h-[400px]">
        {/* Step 1: Certificate Type */}
        {step === 1 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">
                What type of certificate are you issuing?
              </h3>
              <p className="text-muted-foreground mt-1">
                Select the origin of the achievement to map the source details.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto pt-4">
              {[
                {
                  value: "track",
                  icon: GraduationCap,
                  label: "Learning Track",
                  desc: "For completing a full learning track or curriculum.",
                },
                {
                  value: "course",
                  icon: BookOpen,
                  label: "Single Course",
                  desc: "For finishing an individual course module.",
                },
              ].map((t) => (
                <button
                  key={t.value}
                  onClick={() => setCertType(t.value as any)}
                  className={`relative p-6 rounded-2xl border-2 text-left transition-all duration-200 overflow-hidden group ${
                    certType === t.value
                      ? "border-primary shadow-md bg-card"
                      : "border-transparent bg-muted/40 hover:bg-muted"
                  }`}
                >
                  {certType === t.value && (
                    <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
                  )}
                  <div
                    className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 ${certType === t.value ? "bg-primary/10 text-primary" : "bg-muted-foreground/10 text-muted-foreground"}`}
                  >
                    <t.icon className="h-6 w-6" />
                  </div>
                  <h4 className="font-bold text-lg mb-1">{t.label}</h4>
                  <p className="text-sm text-muted-foreground">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Configure */}
        {step === 2 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">Configure Details</h3>
              <p className="text-muted-foreground mt-1">
                Specify exactly what achievement this certificate represents.
              </p>
            </div>

            <Card className="border-muted/70 shadow-sm rounded-2xl">
              <CardContent className="p-6 space-y-6">
                {certType === "track" && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Select Learning Track <span className="text-destructive">*</span>
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      The certificate will display the track's official name unless overridden.
                    </p>
                    <Select value={trackId} onValueChange={setTrackId}>
                      <SelectTrigger className="h-12 rounded-xl text-base text-foreground">
                        <SelectValue placeholder="Search or choose a track..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tracks.map((t: any) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {certType === "course" && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Select Course <span className="text-destructive">*</span>
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      The certificate will display the course's official title unless overridden.
                    </p>
                    <Select value={courseId} onValueChange={setCourseId}>
                      <SelectTrigger className="h-12 rounded-xl text-base text-foreground">
                        <SelectValue placeholder="Search or choose a course..." />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((c: any) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="border-t pt-6 space-y-5">
                  <h4 className="font-bold text-sm text-muted-foreground">
                    Optional Overrides — leave blank to use the official values shown below
                  </h4>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">
                      Certificate Title{" "}
                      <span className="font-normal text-muted-foreground">(optional override)</span>
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      {defaultTitle
                        ? `Default: "${defaultTitle}" — leave blank to use it.`
                        : "Leave blank to use the official course/track name."}
                    </p>
                    <Input
                      className="h-11 rounded-xl text-foreground"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder={
                        defaultTitle || "e.g. Specialist Certification in Advanced React"
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">
                        Training Hours{" "}
                        <span className="font-normal text-muted-foreground">
                          (optional override)
                        </span>
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        {defaultHours != null
                          ? `Default: ${defaultHours} hours — leave blank to use it.`
                          : certType === "track"
                            ? "Tracks have no default duration — enter hours to show them."
                            : "Leave blank to use the official duration."}
                      </p>
                      <Input
                        className="h-11 rounded-xl text-foreground"
                        type="number"
                        value={customHours}
                        onChange={(e) => setCustomHours(e.target.value)}
                        placeholder={defaultHours != null ? String(defaultHours) : "e.g. 40"}
                      />
                    </div>
                    <div className="flex items-start space-x-3 pt-6">
                      <Checkbox
                        id="force-issue"
                        checked={forceIssue}
                        onCheckedChange={(checked) => setForceIssue(!!checked)}
                        className="mt-1"
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="force-issue"
                          className="text-sm font-semibold leading-none cursor-pointer text-foreground"
                        >
                          Force Issuance
                        </label>
                        <p className="text-xs text-muted-foreground">
                          Skip validation checks for student course completion rules.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Recipients */}
        {step === 3 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">Who is receiving this?</h3>
              <p className="text-muted-foreground mt-1">
                Configure whether you want to issue to a single student or perform a bulk issuance.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-2xl">
              {[
                {
                  value: "single",
                  icon: User,
                  label: "Single Student",
                  desc: "Issue to one selected student",
                },
                {
                  value: "team",
                  icon: Users,
                  label: "Group",
                  desc: "Issue to all members of a group",
                },
              ].map((m: any) => (
                <button
                  key={m.value}
                  onClick={() => setRecipientMode(m.value)}
                  className={`p-4 rounded-xl border-2 flex items-start gap-4 text-left transition-all duration-200 ${
                    recipientMode === m.value
                      ? "border-primary bg-primary/5 shadow-sm"
                      : "border-border bg-card hover:border-primary/40"
                  }`}
                >
                  <div
                    className={`mt-0.5 p-2 rounded-full ${recipientMode === m.value ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                  >
                    <m.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{m.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{m.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Recipient Search/Selectors */}
            <div className="bg-card border border-muted/70 rounded-2xl p-6 shadow-sm min-h-[150px]">
              {recipientMode === "" && (
                <div className="text-center text-muted-foreground py-8">
                  Select an option above to configure recipients.
                </div>
              )}

              {recipientMode === "single" && (
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Find Student</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9 h-11 rounded-xl text-foreground"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name or email address..."
                    />
                  </div>
                  {users.length > 0 && (
                    <div className="mt-3 border rounded-xl max-h-60 overflow-y-auto divide-y bg-background shadow-md">
                      {users.map((u: any) => (
                        <button
                          key={u.id}
                          onClick={() => {
                            setSelectedUserId(u.id);
                            setUserSearch(u.full_name || u.email);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-muted/50 flex items-center gap-3 transition-colors ${
                            selectedUserId === u.id
                              ? "bg-primary/5 border-l-4 border-l-primary"
                              : "border-l-4 border-l-transparent"
                          }`}
                        >
                          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                            {(u.full_name || "U").charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-sm text-foreground">
                              {u.full_name || "No name provided"}
                            </p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          {selectedUserId === u.id && (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {recipientMode === "team" && (
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Select Group</Label>
                  <p className="text-xs text-muted-foreground">
                    Every member of this group will receive a certificate. If "Force" is unchecked,
                    only members who completed the requirements are issued one.
                  </p>
                  <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                    <SelectTrigger className="h-11 rounded-xl text-foreground">
                      <SelectValue placeholder="Choose a group..." />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((t: any) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Review & Issue */}
        {step === 4 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">Final Review</h3>
              <p className="text-muted-foreground mt-1">
                Please confirm the details below before issuing.
              </p>
            </div>

            <div className="max-w-2xl">
              <div className="space-y-6">
                <Card className="border-muted/70 shadow-sm rounded-2xl overflow-hidden">
                  <CardHeader className="bg-muted/30 pb-4 border-b">
                    <CardTitle className="text-base flex items-center gap-2 font-bold text-foreground">
                      <FileText className="h-4 w-4 text-primary" /> Issuance Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <dl className="space-y-4">
                      <div className="grid grid-cols-3 gap-2 border-b border-border/50 pb-4">
                        <dt className="text-sm text-muted-foreground font-semibold">Type</dt>
                        <dd className="col-span-2 text-sm font-bold capitalize flex items-center gap-2 text-foreground">
                          {certType === "track" && (
                            <GraduationCap className="h-4 w-4 text-primary" />
                          )}
                          {certType === "course" && <BookOpen className="h-4 w-4 text-accent" />}
                          {certType}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 gap-2 border-b border-border/50 pb-4">
                        <dt className="text-sm text-muted-foreground font-semibold">
                          Recipient(s)
                        </dt>
                        <dd className="col-span-2 text-sm font-bold text-foreground">
                          {recipientMode === "single" ? (
                            <span className="flex items-center gap-2">
                              <User className="h-4 w-4 text-primary" /> Single User
                            </span>
                          ) : (
                            <span className="flex items-center gap-2 text-accent font-bold">
                              <Users className="h-4 w-4" /> Group
                            </span>
                          )}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 gap-2 border-b border-border/50 pb-4">
                        <dt className="text-sm text-muted-foreground font-semibold">Target</dt>
                        <dd className="col-span-2 text-sm font-bold bg-muted/60 text-foreground px-2.5 py-1 rounded-md inline-block max-w-fit border">
                          {getPreviewRecipient()}
                        </dd>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pb-2">
                        <dt className="text-sm text-muted-foreground font-semibold">Force Issue</dt>
                        <dd className="col-span-2 text-sm font-bold text-foreground">
                          {forceIssue ? "Yes (Skip Completion Rules)" : "No (Enforce Completion)"}
                        </dd>
                      </div>
                    </dl>
                  </CardContent>
                </Card>

                {recipientMode !== "single" && (
                  <div className="bg-warning/10 border border-warning/20 text-warning p-4 rounded-2xl flex gap-3">
                    <Package className="h-5 w-5 shrink-0 text-warning mt-0.5" />
                    <div>
                      <h5 className="font-bold text-sm">Bulk Operation</h5>
                      <p className="text-xs mt-1 opacity-90 leading-relaxed">
                        You are about to queue bulk certificate issuance. PDF generation will run
                        asynchronously on the server. Idempotency checks will skip users with
                        existing active certificates.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          className={`gap-2 px-6 rounded-xl ${step === 1 ? "invisible" : ""}`}
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>

        {step < 4 ? (
          <Button
            onClick={() => setStep((s) => s + 1)}
            disabled={
              (step === 1 && !canProceedStep1) ||
              (step === 2 && !canProceedStep2) ||
              (step === 3 && !canProceedStep3)
            }
            className="gap-2 px-8 rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground font-semibold shadow-sm cursor-pointer"
          >
            Continue <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isPending}
            size="lg"
            className="gap-2 px-10 rounded-xl bg-primary hover:bg-primary/95 text-primary-foreground shadow-md font-bold cursor-pointer"
          >
            {isPending && <Loader2 className="h-5 w-5 animate-spin" />}
            {recipientMode === "single" ? (
              <>
                <Award className="h-5 w-5" /> Issue Certificate
              </>
            ) : (
              <>
                <Package className="h-5 w-5" /> Execute Bulk Issuance
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────── Tab 3: Batch History ──────────────────────── */

function BatchHistory() {
  const pagination = usePaginationState({ initialPageSize: 20 });
  const { pageNumber, pageSize, search: batchSearch, setSearch: setBatchSearch, setPageNumber, setPageSize } =
    pagination;
  const { data, isLoading, isFetching } = useCertificateBatchesQuery(pageNumber, pageSize);
  const batchList = data?.items ?? [];

  const [batchTypeFilter, setBatchTypeFilterState] = useState("all");
  // Type filter is applied client-side (over the current page) — the batches endpoint has
  // no server-side type param — so changing it should still reset back to page 1.
  const setBatchTypeFilter = (value: string) => {
    setBatchTypeFilterState(value);
    setPageNumber(1);
  };

  const filteredBatches = useMemo(() => {
    return batchList.filter((b) => {
      // 1. Label Search
      if (batchSearch.trim()) {
        const query = batchSearch.toLowerCase();
        const label = (b.label || b.name || "Bulk Batch").toLowerCase();
        if (!label.includes(query)) return false;
      }

      // 2. Type Filter
      if (batchTypeFilter !== "all" && b.cert_type !== batchTypeFilter) return false;

      return true;
    });
  }, [batchList, batchSearch, batchTypeFilter]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-success-muted text-success border border-success/20 shadow-none hover:bg-success-muted">
            Completed
          </Badge>
        );
      case "completedwitherrors":
        return (
          <Badge
            variant="outline"
            className="border-destructive/20 text-destructive bg-destructive/10"
          >
            Completed with Errors
          </Badge>
        );
      case "running":
        return (
          <Badge
            variant="outline"
            className="border-primary/20 text-primary bg-primary/10 animate-pulse"
          >
            <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
            Running
          </Badge>
        );
      case "queued":
      default:
        return (
          <Badge variant="outline" className="border-warning/20 text-warning bg-warning/10">
            Queued
          </Badge>
        );
    }
  };

  return (
    <Card className="rounded-2xl border border-muted/70 shadow-sm overflow-hidden">
      <CardHeader className="pb-4">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <div>
              <CardTitle className="text-lg font-bold text-foreground">
                Bulk Issuance History ({data?.totalCount ?? filteredBatches.length})
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Track all bulk certificate generation operations.
              </CardDescription>
            </div>
            {(batchSearch || batchTypeFilter !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setBatchSearch("");
                  setBatchTypeFilter("all");
                }}
                className="text-xs text-muted-foreground hover:text-destructive h-8 px-2.5 rounded-lg"
              >
                <FilterX className="h-3.5 w-3.5 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Input */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
              <Input
                placeholder="Search by label or name..."
                value={batchSearch}
                onChange={(e) => setBatchSearch(e.target.value)}
                className="pl-9 h-10 rounded-xl bg-muted/20 border-muted/70 focus-visible:ring-primary focus-visible:bg-background transition-all text-xs text-foreground"
              />
            </div>

            {/* Type Selector */}
            <Select value={batchTypeFilter} onValueChange={setBatchTypeFilter}>
              <SelectTrigger className="w-44 h-10 rounded-xl bg-muted/20 border-muted/70 focus:ring-primary text-xs text-foreground">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="track">Learning Track</SelectItem>
                <SelectItem value="course">Single Course</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 border-t">
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={6} columns={6} />
          </div>
        ) : filteredBatches.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground bg-muted/5">
            <Package className="h-10 w-10 mx-auto mb-3 opacity-30 text-primary" />
            <p className="text-sm font-medium">No bulk issuances found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Succeeded</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y">
                {filteredBatches.map((b) => (
                  <TableRow key={b.id} className="hover:bg-muted/10 transition-colors">
                    <TableCell className="font-semibold text-sm text-foreground">
                      {b.label || b.name || "Bulk Batch"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className="capitalize text-[10px] font-bold border"
                      >
                        {b.cert_type}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(b.status)}</TableCell>
                    <TableCell className="font-mono text-sm font-bold text-foreground">
                      {b.issued_count} / {b.total_count}
                    </TableCell>
                    <TableCell className="font-mono text-sm font-semibold text-destructive">
                      {b.failed_count}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-medium">
                      {new Date(b.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && (
          <PaginationControls
            pageNumber={data.pageNumber}
            pageSize={data.pageSize}
            totalPages={data.totalPages}
            totalCount={data.totalCount}
            hasNextPage={data.hasNextPage}
            hasPreviousPage={data.hasPreviousPage}
            onPageChange={setPageNumber}
            onPageSizeChange={setPageSize}
            isLoading={isFetching}
          />
        )}
      </CardContent>
    </Card>
  );
}
